import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  Settings as SettingsIcon,
  Zap,
  CreditCard,
  Bell,
  Link2,
  Check,
  AlertCircle,
  Loader2,
  X,
} from "lucide-react";
import { useDocumentTitle } from "usehooks-ts";
import { useEffect } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkflowUsage } from "@/queries/workflow";
import {
  useProviders,
  useUserConnections,
  useConnectProvider,
  useDisconnectProvider,
} from "@/queries/providers";
import type { ProviderConnectionStatus } from "@/lib/api/providers";

interface SettingsSearch {
  connected?: string;
  error?: string;
}

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    connected:
      typeof search.connected === "string" ? search.connected : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
});

function getStatusBadge(status: ProviderConnectionStatus) {
  switch (status) {
    case "ACTIVE":
      return (
        <Badge
          variant="default"
          className="bg-green-500/20 text-green-600 border-green-500/30"
        >
          Active
        </Badge>
      );
    case "EXPIRED":
      return (
        <Badge
          variant="secondary"
          className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30"
        >
          Expired
        </Badge>
      );
    case "REVOKED":
      return <Badge variant="destructive">Revoked</Badge>;
    case "ERROR":
      return <Badge variant="destructive">Error</Badge>;
    default:
      return null;
  }
}

function SettingsPage() {
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const { connected, error } = useSearch({ from: "/settings" });

  const { data: usage, isLoading: usageLoading } = useWorkflowUsage();
  const { data: providersData, isLoading: providersLoading } = useProviders();
  const { data: connectionsData, isLoading: connectionsLoading } =
    useUserConnections();

  const connectMutation = useConnectProvider();
  const disconnectMutation = useDisconnectProvider();

  useEffect(() => {
    if (connected) {
      toast.success(`Successfully connected to ${connected}`, {
        description: "You can now sync data from this provider.",
      });
      navigate({
        to: "/settings",
        search: { connected: undefined, error: undefined },
        replace: true,
      });
    } else if (error) {
      toast.error("Connection failed", {
        description: error,
      });
      navigate({
        to: "/settings",
        search: { connected: undefined, error: undefined },
        replace: true,
      });
    }
  }, [connected, error, navigate]);

  useDocumentTitle("Settings | Notebook");

  if (!user) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <SettingsIcon className="h-16 w-16 text-muted-foreground mx-auto" />
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">Settings</h2>
            <p className="text-muted-foreground">Sign in to view settings</p>
          </div>
        </div>
      </div>
    );
  }

  const usagePercentage =
    usage && !usage.unlimited
      ? Math.round((usage.count / usage.limit) * 100)
      : 0;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-2 p-4 md:p-6 pb-4 border-b border-border/40">
        <div className="flex items-center gap-3">
          <SettingsIcon className="h-6 w-6" />
          <h1 className="text-2xl font-semibold">Settings</h1>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-6 overflow-y-auto">
        <div className="max-w-2xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Workflow Automation
              </CardTitle>
              <CardDescription>
                AI-powered automation for processing notes in lists
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Monthly usage
                </span>
                {usageLoading ? (
                  <span className="text-sm text-muted-foreground">
                    Loading...
                  </span>
                ) : usage?.unlimited ? (
                  <span className="text-sm font-medium">Unlimited</span>
                ) : (
                  <span className="text-sm font-medium">
                    {usage?.count ?? 0} / {usage?.limit ?? 100} workflows
                  </span>
                )}
              </div>

              {!usageLoading && usage && !usage.unlimited && (
                <div className="space-y-2">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {usage.remaining} workflows remaining this month (
                    {usage.month})
                  </p>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                Workflows help automate repetitive tasks like splitting shopping
                lists into individual items and fetching product prices.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Billing
              </CardTitle>
              <CardDescription>
                Manage your subscription and billing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Billing settings are not yet available.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
              </CardTitle>
              <CardDescription>
                Configure how you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Notification settings are not yet available.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Connected Apps
              </CardTitle>
              <CardDescription>
                Connect external services to sync data with your notebook
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {providersLoading || connectionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : providersData?.providers?.length ? (
                <div className="space-y-3">
                  {providersData.providers.map((provider) => {
                    const connection = connectionsData?.connections?.find(
                      (c) => c.provider.slug === provider.slug,
                    );
                    const isConnected = connection?.status === "ACTIVE";
                    const isConnecting =
                      connectMutation.isPending &&
                      connectMutation.variables === provider.slug;
                    const isDisconnecting =
                      disconnectMutation.isPending &&
                      disconnectMutation.variables === provider.slug;

                    return (
                      <div
                        key={provider.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-border/60 bg-card"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                            style={{ backgroundColor: `${provider.color}20` }}
                          >
                            {provider.icon}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {provider.name}
                              </span>
                              {connection && getStatusBadge(connection.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {provider.description}
                            </p>
                            {connection?.lastSyncAt && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Last synced:{" "}
                                {new Date(
                                  connection.lastSyncAt,
                                ).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {isConnected ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                disconnectMutation.mutate(provider.slug)
                              }
                              disabled={isDisconnecting}
                            >
                              {isDisconnecting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                              <span className="ml-1">Disconnect</span>
                            </Button>
                          ) : (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() =>
                                connectMutation.mutate(provider.slug)
                              }
                              disabled={isConnecting}
                            >
                              {isConnecting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                              <span className="ml-1">Connect</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No data providers available</p>
                </div>
              )}

              <p className="text-sm text-muted-foreground pt-2">
                Connected apps can sync data like workouts, music listening
                history, and more into your note lists.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
